import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchSportTypeCatalog,
  flattenSportCatalog,
  invalidateSportTypeCatalogCache,
  isSportSelectable
} from '../api/sportTypes';
import './sport-type-selector.css';

/**
 * Выбор вида спорта: вкладки групп + панель видов спорта (без вложенного аккордеона).
 */
export default function SportTypeSelector({
  value,
  values = [],
  onChange,
  showSoon = true,
  demoOnly = false,
  compact = false,
  multiple = false
}) {
  const { t, i18n } = useTranslation('sportTypeSelector');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeGroupId, setActiveGroupId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    invalidateSportTypeCatalogCache();
    fetchSportTypeCatalog()
      .then((data) => {
        if (!cancelled) {
          setGroups(data);
          setError('');
        }
      })
      .catch(() => {
        if (!cancelled) setError(t('sportTypeSelector:loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [i18n.language, t]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groups
      .map((g) => {
        let sports = g.sports;
        if (demoOnly) sports = sports.filter((s) => s.hasDemoSchool);
        if (q) {
          sports = sports.filter((s) =>
            s.name.toLowerCase().includes(q)
            || (s.venueName && s.venueName.toLowerCase().includes(q))
            || g.groupName.toLowerCase().includes(q)
          );
        }
        if (!showSoon) sports = sports.filter((s) => isSportSelectable(s));
        return { ...g, sports };
      })
      .filter((g) => g.sports.length > 0);
  }, [groups, search, demoOnly, showSoon]);

  const searching = search.trim().length > 0;

  useEffect(() => {
    if (filteredGroups.length === 0) {
      setActiveGroupId(null);
      return;
    }
    if (searching) return;
    const exists = filteredGroups.some((g) => g.groupId === activeGroupId);
    if (!exists) {
      setActiveGroupId(filteredGroups[0].groupId);
    }
  }, [filteredGroups, searching, activeGroupId]);

  const flat = useMemo(() => flattenSportCatalog(filteredGroups), [filteredGroups]);
  const activeGroup = filteredGroups.find((g) => g.groupId === activeGroupId) || filteredGroups[0];

  function isSelected(sport) {
    if (multiple) return values.map(String).includes(String(sport.id));
    return String(value) === String(sport.id);
  }

  function pick(sport) {
    if (!isSportSelectable(sport)) return;
    if (multiple) {
      const id = sport.id;
      const nextIds = isSelected(sport)
        ? values.filter((v) => String(v) !== String(id))
        : [...values, id];
      onChange(nextIds, flat.filter((s) => nextIds.map(String).includes(String(s.id))));
      return;
    }
    onChange(sport.id, sport);
  }

  function renderSportCard(sport) {
    const selected = isSelected(sport);
    const soon = sport.launchPriority >= 3;
    const disabled = soon || !isSportSelectable(sport);

    return (
      <button
        key={sport.id}
        type="button"
        disabled={disabled}
        onClick={() => pick(sport)}
        className={`sport-type-selector__sport${selected ? ' is-selected' : ''}`}
      >
        {soon && (
          <span className="sport-type-selector__badge sport-type-selector__badge--soon">{t('sportTypeSelector:soon')}</span>
        )}
        {sport.hasDemoSchool && !soon && (
          <span className="sport-type-selector__badge sport-type-selector__badge--demo">{t('sportTypeSelector:demo')}</span>
        )}
        <span className="sport-type-selector__sport-emoji">{sport.emoji}</span>
        <span className="sport-type-selector__sport-name">{sport.name}</span>
        {sport.venueName && (
          <span className="sport-type-selector__sport-venue">{sport.venueName}</span>
        )}
      </button>
    );
  }

  function renderSearchItem(sport, group) {
    const selected = isSelected(sport);
    const soon = sport.launchPriority >= 3;
    const disabled = soon || !isSportSelectable(sport);

    return (
      <button
        key={`${group.groupId}-${sport.id}`}
        type="button"
        disabled={disabled}
        onClick={() => pick(sport)}
        className={`sport-type-selector__search-item${selected ? ' is-selected' : ''}`}
      >
        <span className="sport-type-selector__search-item-emoji">{sport.emoji}</span>
        <span className="sport-type-selector__search-item-body">
          <span className="sport-type-selector__search-item-name">
            {sport.name}
            {soon && t('sportTypeSelector:soonSuffix')}
            {sport.hasDemoSchool && !soon && t('sportTypeSelector:demoSuffix')}
          </span>
          <span className="sport-type-selector__search-item-group">
            {group.groupEmoji} {group.groupName}
            {sport.venueName ? ` · ${sport.venueName}` : ''}
          </span>
        </span>
      </button>
    );
  }

  if (loading) {
    return <p style={{ color: '#888', fontSize: 14, margin: 0 }}>{t('sportTypeSelector:loading')}</p>;
  }
  if (error) {
    return <p style={{ color: '#c62828', fontSize: 13, margin: 0 }}>{error}</p>;
  }
  // Пустой результат ПОИСКА не должен убирать поле ввода — иначе опечатку нельзя стереть.
  if (flat.length === 0 && !searching) {
    return <p style={{ color: '#888', fontSize: 13, margin: 0 }}>{t('sportTypeSelector:notFound')}</p>;
  }

  const rootClass = `sport-type-selector${compact ? ' sport-type-selector--compact' : ''}`;

  return (
    <div className={rootClass}>
      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('sportTypeSelector:searchPlaceholder')}
        className="sport-type-selector__search"
      />

      {searching ? (
        <div className="sport-type-selector__search-results">
          {flat.length === 0 && (
            <p style={{ color: '#888', fontSize: 13, margin: '8px 0 0' }}>{t('sportTypeSelector:notFound')}</p>
          )}
          {filteredGroups.flatMap((group) =>
            group.sports.map((sport) => renderSearchItem(sport, group))
          )}
        </div>
      ) : (
        <>
          <p className="sport-type-selector__hint">
            {t('sportTypeSelector:hint')}
          </p>

          <div className="sport-type-selector__groups" role="tablist" aria-label={t('sportTypeSelector:categoriesAria')}>
            {filteredGroups.map((group) => {
              const active = group.groupId === activeGroup?.groupId;
              const hasSelected = group.sports.some((s) => isSelected(s));
              return (
                <button
                  key={group.groupId}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveGroupId(group.groupId)}
                  className={`sport-type-selector__group-tab${active ? ' is-active' : ''}`}
                  style={active ? { borderColor: group.color || undefined } : undefined}
                >
                  <span className="sport-type-selector__group-tab-emoji">{group.groupEmoji}</span>
                  <span>{group.groupName}</span>
                  {hasSelected && !active && (
                    <span style={{ fontSize: 10, color: '#EB3333' }}>✓</span>
                  )}
                </button>
              );
            })}
          </div>

          {activeGroup && (
            <section
              className="sport-type-selector__panel"
              role="tabpanel"
              aria-label={activeGroup.groupName}
            >
              <div
                className="sport-type-selector__panel-head"
                style={{ borderLeftColor: activeGroup.color || '#EB3333' }}
              >
                <span style={{ fontSize: 22 }}>{activeGroup.groupEmoji}</span>
                <span className="sport-type-selector__panel-title">{activeGroup.groupName}</span>
                <span className="sport-type-selector__panel-count">
                  {t('sportTypeSelector:sportsCount', { count: activeGroup.sports.length })}
                </span>
              </div>
              <div className="sport-type-selector__sports">
                {activeGroup.sports.map((sport) => renderSportCard(sport))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
